import Authentication from '@/utilities/auth/Authentication'
import AxiosServiceInterface from '@/utilities/service/interfaces/AxiosServiceInterface'
import { Store, Commit } from 'vuex'
// eslint-disable-next-line prettier/prettier
import type { VueJwtDecodeType } from 'vue-jwt-decode'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosHeaders } from 'axios'
import TokenManager from '@/utilities/auth/azure/TokenManager'
import RequestWatcher from '@/utilities/auth/azure/RequestWatcher'
import ResponseWatcher from '@/utilities/auth/azure/ResponseWatcher'
import AzurePluginInterface from '@/utilities/auth/azure/interfaces/AzurePluginInterface'
import RefreshTokenInterface from '@/utilities/auth/azure/interfaces/RefreshTokenInterface'

class AxiosService implements AxiosServiceInterface {
  axios: AxiosInstance
  VueJwtDecode: VueJwtDecodeType

  constructor(axios: AxiosInstance, VueJwtDecode: VueJwtDecodeType) {
    this.axios = axios
    this.VueJwtDecode = VueJwtDecode
  }

  setResponseInspector(store: Store<Commit>): void {
    this.axios.interceptors.response.use(
      (response: AxiosResponse) => {
        const token = response?.headers['x-opus']
        if (token) {
          store.commit('Auth/setOpusPayload', { token, payload: this.VueJwtDecode.decode(token) })
        }
        return response
      },
      (error: any) => {
        return Promise.reject(error)
      },
    )
  }

  displayResponseMessages(store: Store<Commit>): void {
    this.axios.interceptors.response.use(
      (response: AxiosResponse) => {
        const message = response?.data?.message ?? response?.data?.data?.message ?? null

        if (message) {
          store.commit('Dashboard/setFeedback', { message }, { root: true })
        }

        return response
      },
      (error: any) => Promise.reject(error),
    )
  }

  refreshTokenWatcher(azure: AzurePluginInterface, tokenManager: TokenManager, store: Store<Commit>): void {
    const payload = this.VueJwtDecode.decode(tokenManager.getTokens().token)

    // if we have a fake token we should not refresh it
    if (['development', 'test'].includes(process.env.NODE_ENV ?? '') && payload?.env === 'test') {
      return
    }

    const watchers: RefreshTokenInterface[] = [
      new RequestWatcher(azure, tokenManager, this.VueJwtDecode),
      new ResponseWatcher(azure, tokenManager, this.VueJwtDecode),
    ]

    watchers.forEach(watcher =>
      watcher.watch(
        this,
        (token: string, request?: AxiosRequestConfig) => {
          const auth = new Authentication({ token, refresh: token }, this.VueJwtDecode)

          this.setAuthorization(auth)

          if (request?.headers) {
            // @ts-ignore
            request.headers.set('Authorization', auth.getAuthorization())
          }

          store.commit('Auth/SET_TOKEN', token)
        },
        async () => {
          await store.dispatch('Auth/logout')
        },
      ),
    )
  }

  setAuthorization(auth: Authentication): void {
    this.axios.defaults.headers.common.Authorization = auth.getAuthorization()
  }
}

export default AxiosService
